import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError } from "@/api/client";
import { Navigate } from "react-router-dom";
import { normalizeGameToSnake } from "@/api/normalize";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatKickoff } from "@/lib/time";
import { WM_GROUPS, PHASE_LABELS } from "@/lib/constants";
import { loadGroupTeams, type GroupTeam } from "@/lib/bonus";
import { teamDe } from "@/lib/teamNames";
import { PlayerCombobox } from "@/components/PlayerCombobox";
import StoryGeneratorPanel from "@/components/admin/StoryGeneratorPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import TestPanel from "@/components/admin/TestPanel";
// ScoringSimulator was removed from the admin UI — the rules are stable
// and the live-bonus + test-game flow makes a simulator unnecessary.
// The component file stays in the repo in case we want it back later.
import UserManagement from "@/components/admin/UserManagement";
import WMResetPanel from "@/components/admin/WMResetPanel";
import MonitoringPanel from "@/components/admin/MonitoringPanel";
import { CreateTestGameDialog } from "@/components/admin/CreateTestGameDialog";
import { ImportFixtureDialog } from "@/components/admin/ImportFixtureDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Settings as SettingsIcon, Calendar, Trophy, Users as UsersIcon, Image, Globe, Trash2, FlaskConical, Radio, CheckCircle2, RefreshCw, Gauge } from "lucide-react";

// Resolve a stored bonus-truth team name onto one of the canonical option
// values. teamDe() normalises both sides to German, so a stored "Spanien"
// matches the option whose value is "Spain". An unrecognised value is
// returned unchanged rather than dropped — the select renders it as an extra
// option (see teamOptions) so a hand-entered answer can never be silently
// lost by simply opening the page.
function matchTeam(stored: string | null | undefined, candidates: string[]): string {
  const s = (stored ?? "").trim();
  if (!s) return "";
  return candidates.find((c) => teamDe(c) === teamDe(s)) ?? s;
}

// Options for a team select: the real fixture teams, plus the current value
// if it isn't one of them (so an unmapped legacy answer stays visible and
// round-trips instead of resetting to "—").
function teamOptions(teams: GroupTeam[], current: string): GroupTeam[] {
  if (!current || teams.some((t) => t.name === current)) return teams;
  return [...teams, { name: current, flag: "❓" }];
}

export default function Admin() {
  const { isAdmin, loading, user } = useAuth();
  const [games, setGames] = useState<any[]>([]);
  const [scores, setScores] = useState<Record<string, { h: string; a: string }>>({});
  const [userTotal, setUserTotal] = useState(0);
  const [phaseFilter, setPhaseFilter] = useState<string>("all");

  // Bonus
  const [bWorld, setBWorld] = useState("");
  const [bScorer, setBScorer] = useState("");
  const [bGroups, setBGroups] = useState<Record<string, string>>({});
  const [bonusBusy, setBonusBusy] = useState(false);
  // Austria-themed bonus truth (admin enters after tournament).
  const [bAtBetterThanDe, setBAtBetterThanDe] = useState<"" | "yes" | "no">("");
  const [bAtGroupPosition, setBAtGroupPosition] = useState<"" | "1" | "2" | "3" | "4">("");
  const [bAtFirstGoalScorer, setBAtFirstGoalScorer] = useState("");
  const [bAtTopScorer, setBAtTopScorer] = useState("");
  const [bAtProgression, setBAtProgression] = useState<string>("");
  const [bAtGoalsTotal, setBAtGoalsTotal] = useState<string>("");
  // Teams straight from the fixtures — the SAME source the tippers picked
  // from, so the option values are the canonical API-Football names the tips
  // actually store. The old hardcoded German WM_TEAMS list compared
  // "Brasilien" against a stored "Brazil" and matched nobody.
  const [bTeams, setBTeams] = useState<Record<string, GroupTeam[]>>({});
  const [bonusResult, setBonusResult] = useState<any>(null);
  // The truth as it currently stands on the server, kept so we can refuse to
  // silently blank out an already-resolved answer (see resolveBonus).
  const [bTruthOriginal, setBTruthOriginal] = useState<any>(null);

  // Football API sync
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  useEffect(() => { document.title = "Admin – Englputzeder Tippspiel"; }, []);

  // Flat, de-duplicated team list for the Weltmeister picker (canonical
  // API-Football names as values, German only as the visible label).
  const allTeams = useMemo(() => {
    const m = new Map<string, string>();
    Object.values(bTeams).flat().forEach((t) => m.set(t.name, t.flag));
    // Keep an unmapped stored champion selectable rather than silently "—".
    if (bWorld && !m.has(bWorld)) m.set(bWorld, "❓");
    return Array.from(m.entries()).sort((a, b) => teamDe(a[0]).localeCompare(teamDe(b[0])));
  }, [bTeams, bWorld]);

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
  }, [isAdmin]);

  const refresh = async () => {
    const [gRaw, userPage, bt, teams] = await Promise.all([
      api.games.list() as Promise<any[]>,
      // Just need the accurate total for the header count — limit:1 keeps
      // the payload tiny. The full paginated list lives in UserManagement.
      api.admin.listUsers({ limit: 1 }).catch(() => ({ total: 0 } as any)),
      api.bonus.truth().catch(() => null),
      loadGroupTeams().catch(() => ({} as Record<string, GroupTeam[]>)),
    ]);
    setBTeams(teams);
    const g = (gRaw ?? []).map(normalizeGameToSnake);
    g.sort((a: any, b: any) => (a.game_no ?? 0) - (b.game_no ?? 0));
    setGames(g);
    setUserTotal(userPage?.total ?? 0);
    const init: Record<string, {h:string;a:string}> = {};
    g.forEach((x: any) => {
      init[x.id] = { h: x.home_score?.toString() ?? "", a: x.away_score?.toString() ?? "" };
    });
    setScores(init);
    setBTruthOriginal(bt);
    if (bt) {
      const t = bt as any;
      // Map a previously-resolved truth onto the CANONICAL option values.
      // Older resolutions stored German labels ("Spanien") because the form
      // used to be fed from the hardcoded German WM_TEAMS list, while the
      // options are now the fixture names ("Spain"). Without this the select
      // finds no matching <option> and silently renders "—" — and saving
      // would then wipe that answer. Only USA/England survived unmapped,
      // because their German and English spellings are identical.
      const allNames = Object.values(teams).flat().map((x) => x.name);
      setBWorld(matchTeam(t.worldChampion, allNames));
      setBScorer(t.topScorer ?? "");
      const storedGroups = (t.groupWinners ?? {}) as Record<string, string>;
      const mappedGroups: Record<string, string> = {};
      for (const [k, v] of Object.entries(storedGroups)) {
        mappedGroups[k] = matchTeam(v, (teams[k] ?? []).map((x) => x.name));
      }
      setBGroups(mappedGroups);
      setBAtBetterThanDe(t.atBetterThanDe === true ? "yes" : t.atBetterThanDe === false ? "no" : "");
      setBAtGroupPosition(t.atGroupPosition ? String(t.atGroupPosition) as "1"|"2"|"3"|"4" : "");
      setBAtFirstGoalScorer(t.atFirstGoalScorer ?? "");
      setBAtTopScorer(t.atTopScorer ?? "");
      setBAtProgression(t.atProgression ?? "");
      setBAtGoalsTotal(t.atGoalsTotal != null ? String(t.atGoalsTotal) : "");
    }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="py-12 text-center space-y-3">
        <div className="text-5xl">🔒</div>
        <h1 className="font-display font-extrabold text-2xl">Kein Zugriff</h1>
        <p className="text-muted-foreground">Du bist nicht als Admin eingerichtet.</p>
        <p className="text-xs text-muted-foreground">User-ID: <code>{user.id}</code></p>
      </div>
    );
  }

  const save = async (id: string) => {
    const s = scores[id];
    const h = parseInt(s.h, 10), a = parseInt(s.a, 10);
    if (isNaN(h) || isNaN(a) || h < 0 || a < 0) { toast.error("Ungültige Werte"); return; }
    try {
      await api.games.adminUpdate(id, { homeScore: h, awayScore: a, isFinished: true });
      const g = games.find((x) => x.id === id);
      // Activity-feed entry — admin route requires admin/staff role.
      if (g) {
        await api.feed.create(`📣 Endstand: ${g.home_flag} ${g.home_team} ${h}:${a} ${g.away_team} ${g.away_flag}`).catch(() => {});
      }
      toast.success("Ergebnis gespeichert! Punkte werden berechnet.");
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Speichern");
    }
  };

  const resolveBonus = async () => {
    // Refuse to silently drop answers that are already resolved on the server.
    // A blank select submits "" and would clear that question for everyone —
    // exactly what a value the dropdown couldn't render would have caused.
    if (bTruthOriginal) {
      const o = bTruthOriginal as any;
      const cleared: string[] = [];
      if (o.worldChampion && !bWorld) cleared.push("Weltmeister");
      if (o.topScorer && !bScorer) cleared.push("Torschützenkönig");
      for (const [k, v] of Object.entries((o.groupWinners ?? {}) as Record<string, string>)) {
        if (v && !bGroups[k]) cleared.push(`Gruppe ${k}`);
      }
      if (o.atFirstGoalScorer && !bAtFirstGoalScorer) cleared.push("Erstes AT-Tor");
      if (o.atTopScorer && !bAtTopScorer) cleared.push("Bester AT-Torschütze");
      if (o.atProgression && !bAtProgression) cleared.push("AT-Endstation");
      if (o.atBetterThanDe != null && bAtBetterThanDe === "") cleared.push("AT weiter als DE");
      if (o.atGroupPosition != null && bAtGroupPosition === "") cleared.push("AT Gruppenplatz");
      if (o.atGoalsTotal != null && bAtGoalsTotal === "") cleared.push("AT Tore gesamt");
      if (cleared.length > 0) {
        toast.error(
          `${cleared.length} bereits aufgelöste Frage(n) sind im Formular leer: ${cleared.join(", ")}. ` +
          "Bitte befüllen — sonst werden diese Punkte allen Tippern aberkannt.",
          { duration: 12000 },
        );
        return;
      }
    }
    if (!confirm("Bonus-Punkte für alle berechnen? Vorherige Bonus-Punkte werden überschrieben.")) return;
    setBonusBusy(true);
    setBonusResult(null);
    try {
      const res: any = await api.bonus.adminResolve({
        worldChampion: bWorld || null,
        topScorer: bScorer || null,
        groupWinners: bGroups,
        atBetterThanDe: bAtBetterThanDe === "" ? null : bAtBetterThanDe === "yes",
        atGroupPosition: bAtGroupPosition === "" ? null : Number(bAtGroupPosition),
        atFirstGoalScorer: bAtFirstGoalScorer || null,
        atTopScorer: bAtTopScorer || null,
        atProgression: (bAtProgression || null) as any,
        atGoalsTotal: bAtGoalsTotal === "" ? null : Math.max(0, Math.min(99, Number(bAtGoalsTotal))),
      });
      setBonusResult(res);
      // A resolved question that matched ZERO tippers is the fingerprint of a
      // value the tips can never equal — warn loudly instead of a green tick.
      if (res?.unmatched?.length) {
        toast.error(`${res.unmatched.length} Antwort(en) keinem Team/Spieler zuordenbar — Details unten`);
      } else if (res?.awarded === 0 && res?.tippers > 0) {
        toast.error("0 Tipper haben Punkte bekommen — bitte Auswertung unten prüfen");
      } else {
        toast.success(`Bonus-Punkte vergeben ✅ ${res?.awarded ?? 0}/${res?.tippers ?? 0} Tipper`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setBonusBusy(false);
    }
  };

  const syncFootball = async () => {
    setSyncBusy(true);
    setSyncResult(null);
    try {
      const data = await api.admin.syncFootball();
      const msg = data?.mode === "live"
        ? `✅ Live-Sync · ${data.liveUpdated ?? 0} aktualisiert`
        : `✅ ${data?.total ?? 0} Spiele · ${data?.inserted ?? 0} neu · ${data?.updated ?? 0} aktualisiert`;
      setSyncResult(msg);
      toast.success(msg);
      refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Sync fehlgeschlagen";
      toast.error(`Sync fehlgeschlagen: ${msg}`);
      setSyncResult(`❌ ${msg}`);
    } finally {
      setSyncBusy(false);
    }
  };

  const phases = Array.from(new Set(games.map(g => g.phase ?? "group")));
  const filteredGames = phaseFilter === "all" ? games : games.filter(g => (g.phase ?? "group") === phaseFilter);
  // "Deletable" pool covers both manual test games (external_id IS NULL)
  // and ad-hoc imports (group_label = 'EXT'). Real WM fixtures stay
  // immutable.
  const isTestGame = (g: any) => g.external_id == null;
  const isImportedGame = (g: any) => g.external_id != null && g.group_label === "EXT";
  const deletableGames = games.filter((g) => isTestGame(g) || isImportedGame(g));
  const testGames = games.filter(isTestGame);
  // Sort test games so the currently-live ones float to the top — that's
  // where the admin's attention is during a test run. (Manual live-mode
  // controls only apply to test games, not to imports.)
  const sortedTestGames = [...testGames].sort((a, b) => {
    if (!!b.is_live !== !!a.is_live) return b.is_live ? 1 : -1;
    return (a.game_no ?? 0) - (b.game_no ?? 0);
  });

  const deleteTestGame = async (g: any) => {
    const kind = isImportedGame(g) ? "Import-Spiel" : "Test-Spiel";
    if (!confirm(`${kind} #${g.game_no} (${g.home_team} vs ${g.away_team}) wirklich löschen?\nTipps zu diesem Spiel werden ebenfalls entfernt.`)) return;
    try {
      await api.games.adminDelete(g.id);
      toast.success(`${kind} gelöscht 🗑`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Löschen");
    }
  };

  // ---- Test-game live-mode controls -----------------------------------
  // The API-Football cron writes is_live / live_*_score / minute on real
  // fixtures every minute. Test games (external_id IS NULL) are invisible
  // to the cron, so to E2E-test the live-bonus + push pipeline we drive
  // those fields by hand from this panel. The server rejects writes to
  // these fields on non-test games (HTTP 400 live_fields_locked).
  const startLive = async (g: any) => {
    try {
      await api.games.adminUpdate(g.id, {
        isLive: true,
        liveHomeScore: 0,
        liveAwayScore: 0,
        minute: 1,
        statusShort: "1H",
      });
      toast.success(`Live gestartet 🔴 ${g.home_team} 0:0 ${g.away_team}`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Konnte Live-Modus nicht starten");
    }
  };

  const updateLive = async (g: any, h: number, a: number, m: number) => {
    if (!Number.isFinite(h) || !Number.isFinite(a) || !Number.isFinite(m) || h < 0 || a < 0 || m < 0) {
      toast.error("Ungültige Werte");
      return;
    }
    try {
      await api.games.adminUpdate(g.id, {
        liveHomeScore: h,
        liveAwayScore: a,
        minute: m,
      });
      toast.success(`Live aktualisiert: ${h}:${a} (${m}')`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Update fehlgeschlagen");
    }
  };

  const endLive = async (g: any) => {
    const h = g.live_home_score ?? 0;
    const a = g.live_away_score ?? 0;
    if (!confirm(`Spiel beenden? Endstand ${h}:${a} wird gewertet — Punkte werden vergeben und Push-Benachrichtigungen verschickt.`)) return;
    try {
      // Single PATCH sets the final scores → triggers scoreGame() server-side
      // → fans out the push notifications via the BullMQ pipeline.
      await api.games.adminUpdate(g.id, {
        isLive: false,
        homeScore: h,
        awayScore: a,
        isFinished: true,
        statusShort: "FT",
      });
      await api.feed.create(`📣 Test-Spiel beendet: ${g.home_flag} ${g.home_team} ${h}:${a} ${g.away_team} ${g.away_flag}`).catch(() => {});
      toast.success(`Spiel beendet ✅ Endstand ${h}:${a} — Punkte werden vergeben`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Konnte Spiel nicht beenden");
    }
  };

  // Force-resync a single imported fixture from API-Football. Used when
  // the live cron missed the FT transition (e.g. game ended between
  // ticks, or admin imported the fixture after kickoff). Idempotent —
  // import-fixture upserts on externalId.
  const refreshFromApi = async (g: any) => {
    const externalId = g.external_id;
    if (!externalId) {
      toast.error("Spiel hat keine externalId — nicht von API-Football importiert.");
      return;
    }
    try {
      await api.games.adminImportFixture(Number(externalId));
      toast.success(`${g.home_team} vs ${g.away_team}: aktualisiert ✓`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Aktualisierung fehlgeschlagen");
    }
  };

  const rescoreGame = async (g: any) => {
    try {
      const res = await api.admin.scoring.rescore(g.id);
      toast.success(`Punkte neu berechnet: ${res.tipsUpdated} Tipps, ${res.usersUpdated} Tipper aktualisiert ✅`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Neuberechnung fehlgeschlagen");
    }
  };

  const cleanupAllTestGames = async () => {
    if (deletableGames.length === 0) { toast.info("Keine Test- oder Import-Spiele vorhanden."); return; }
    if (!confirm(`${deletableGames.length} Test-/Import-Spiel(e) und deren Tipps löschen?`)) return;
    try {
      const res = await api.games.adminCleanupTests();
      toast.success(`${res.deleted} Spiel(e) gelöscht 🧹`);
      refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Aufräumen");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display font-extrabold text-3xl">Admin 🛡️</h1>
        <span className="text-xs text-muted-foreground">{userTotal} Tipper · {games.length} Spiele</span>
      </div>

      <Tabs defaultValue="settings" className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 h-auto">
          <TabsTrigger value="settings" className="flex items-center gap-1.5">
            <SettingsIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Einstellungen</span>
            <span className="sm:hidden">Config</span>
          </TabsTrigger>
          <TabsTrigger value="games" className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>Spiele</span>
          </TabsTrigger>
          <TabsTrigger value="bonus" className="flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" />
            <span>Bonus</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1.5">
            <UsersIcon className="h-3.5 w-3.5" />
            <span>Tipper</span>
          </TabsTrigger>
          <TabsTrigger value="story" className="flex items-center gap-1.5">
            <Image className="h-3.5 w-3.5" />
            <span>Story</span>
          </TabsTrigger>
          <TabsTrigger value="monitor" className="flex items-center gap-1.5">
            <Gauge className="h-3.5 w-3.5" />
            <span>Monitor</span>
          </TabsTrigger>
        </TabsList>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          <TestPanel />
          <SettingsPanel />
          {/* Pre-WM wipe — pinned at the bottom so admins don't trip over it
              while configuring routine settings. Has its own confirmation
              gate; safe to leave mounted. */}
          <WMResetPanel />
        </TabsContent>

        {/* GAMES = sync trigger + result entry */}
        <TabsContent value="games" className="mt-4 space-y-4">
          <section className="bg-card rounded-lg shadow-card p-4">
            <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
              <Globe className="h-5 w-5 text-accent" /> API-Football Sync
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              Holt aktuellen Spielplan & Live-Ergebnisse der WM 2026 von API-Football. Der Cron-Container
              syncht automatisch jede Minute während Live-Spielen.
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={syncFootball}
                disabled={syncBusy}
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
              >
                {syncBusy ? "Synchronisiere …" : "🔄 Jetzt synchronisieren"}
              </Button>
              {syncResult && <div className="text-xs font-semibold">{syncResult}</div>}
            </div>
          </section>

          {/* TEST GAMES — manual fixtures for E2E testing the full
              lock → score → push pipeline. external_id IS NULL marks them
              as test games; the cron sync only touches games with an
              external_id, so these stay isolated. */}
          <section className="bg-card rounded-lg shadow-card p-4">
            <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-accent" /> Test-Spiele
              {testGames.length > 0 && (
                <span className="text-xs font-bold bg-accent/15 text-accent px-2 py-0.5 rounded-pill">{testGames.length}</span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              Lege ein Test-Spiel an, um den gesamten Ablauf zu prüfen: Tipp-Sperre 15 Min vor Anpfiff,
              Ergebniseingabe → Punktevergabe → Push-Benachrichtigung. Test-Spiele werden vom
              API-Football-Sync ignoriert und können jederzeit gelöscht werden.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <CreateTestGameDialog onCreated={refresh} />
              <ImportFixtureDialog onImported={refresh} />
              {deletableGames.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={cleanupAllTestGames}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 font-bold gap-1.5"
                >
                  <Trash2 className="h-4 w-4" />
                  Alle Test/Import löschen ({deletableGames.length})
                </Button>
              )}
            </div>

            {/* Per-test-game live-mode controls. Driving is_live + the live
                scores by hand fires the same SSE/feed update + scoring +
                push pipeline that the real API-Football cron uses, so the
                whole live-bonus + ranking + notification flow is E2E-
                testable without waiting for a real WM kickoff. */}
            {sortedTestGames.length > 0 && (
              <div className="mt-4 border-t border-border pt-4 space-y-2">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Radio className="h-3.5 w-3.5" /> Live-Modus
                </div>
                {sortedTestGames.map((g) => (
                  <LiveControls key={g.id} game={g} onStart={startLive} onUpdate={updateLive} onEnd={endLive} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="font-display font-bold text-lg">Ergebnisse eintragen</h2>
              <span className="text-xs text-muted-foreground">{filteredGames.length} Spiele</span>
            </div>
            {/* Clarifying banner: most results land here automatically via
                the live + daily syncs. Manual entry exists for tests,
                fallback during API outages, and rare overrides. */}
            <div className="rounded-md bg-accent/10 border border-accent/30 px-3 py-2.5 mb-3 text-[12px] flex items-start gap-2">
              <Globe className="h-4 w-4 text-accent shrink-0 mt-0.5" />
              <div>
                <b>Ergebnisse kommen automatisch von API-Football</b> — sowohl Live-Stand jede Minute während des Spiels als auch der Endstand mit FT/AET/PEN. Die Punktevergabe läuft direkt mit. Diese Tabelle ist nur für{" "}
                <b>Tests, API-Ausfall oder Korrekturen</b> gedacht — im Normalbetrieb musst du hier nichts eintragen.
              </div>
            </div>
            <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
              <button
                onClick={() => setPhaseFilter("all")}
                className={`text-xs font-bold px-3 py-1.5 rounded-pill whitespace-nowrap ${
                  phaseFilter === "all" ? "bg-accent text-accent-foreground" : "bg-card"
                }`}
              >
                Alle ({games.length})
              </button>
              {phases.map((p) => (
                <button
                  key={p}
                  onClick={() => setPhaseFilter(p)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-pill whitespace-nowrap ${
                    phaseFilter === p ? "bg-accent text-accent-foreground" : "bg-card"
                  }`}
                >
                  {PHASE_LABELS[p] ?? p}
                </button>
              ))}
            </div>
            <div className="bg-card rounded-lg shadow-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 w-16">#</th>
                    <th className="text-left px-3 py-2 w-32 hidden md:table-cell">Phase</th>
                    <th className="text-left px-3 py-2">Spiel</th>
                    <th className="text-left px-3 py-2 w-44 hidden lg:table-cell">Anpfiff</th>
                    <th className="text-right px-3 py-2 w-72">Ergebnis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredGames.map((g) => (
                    <tr key={g.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{g.game_no}</td>
                      <td className="px-3 py-2 text-xs hidden md:table-cell">
                        {PHASE_LABELS[g.phase ?? "group"] ?? g.phase}
                        {g.group_label && ` · ${g.group_label}`}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold flex items-center gap-1.5">
                          {isTestGame(g) && (
                            <span title="Test-Spiel" className="text-[10px] font-bold bg-accent/15 text-accent px-1.5 py-0.5 rounded-pill inline-flex items-center gap-0.5">
                              <FlaskConical className="h-2.5 w-2.5" /> TEST
                            </span>
                          )}
                          {isImportedGame(g) && (
                            <span title="Importiert aus API-Football" className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-pill inline-flex items-center gap-0.5">
                              <Globe className="h-2.5 w-2.5" /> IMPORT
                            </span>
                          )}
                          <span>{g.home_flag} {g.home_team} <span className="text-muted-foreground">vs.</span> {g.away_team} {g.away_flag}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground hidden lg:table-cell">
                        {formatKickoff(g.kickoff_time)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Input
                            type="number"
                            min={0}
                            max={20}
                            value={scores[g.id]?.h ?? ""}
                            onChange={(e) => setScores((s) => ({ ...s, [g.id]: { ...s[g.id], h: e.target.value } }))}
                            className="h-9 w-14 text-center font-bold"
                          />
                          <span className="font-bold">:</span>
                          <Input
                            type="number"
                            min={0}
                            max={20}
                            value={scores[g.id]?.a ?? ""}
                            onChange={(e) => setScores((s) => ({ ...s, [g.id]: { ...s[g.id], a: e.target.value } }))}
                            className="h-9 w-14 text-center font-bold"
                          />
                          <Button
                            size="sm"
                            onClick={() => save(g.id)}
                            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                          >
                            {g.is_finished ? "Update" : "Speichern"}
                          </Button>
                          {/* Per-row refresh from API-Football. Available for
                              any unfinished game with an external_id — covers
                              both ad-hoc imports AND real WM games that the
                              live cron might have missed (e.g. FT'd between
                              ticks before the settle-pass picked them up). */}
                          {g.external_id != null && !g.is_finished && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => refreshFromApi(g)}
                              title="Von API-Football aktualisieren (Stand, Endergebnis)"
                              className="h-9 px-2 text-blue-600 hover:text-blue-600 hover:bg-blue-50"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {/* Punkte neu berechnen — for finished games whose
                              scoring may have been missed (idempotent). The
                              fix for the homeScore-pollution bug prevents
                              recurrence; this is the one-click rescue for
                              already-affected games. */}
                          {g.is_finished && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => rescoreGame(g)}
                              title="Punkte für dieses Spiel neu berechnen"
                              className="h-9 px-2 text-amber-600 hover:text-amber-600 hover:bg-amber-50"
                            >
                              <Trophy className="h-4 w-4" />
                            </Button>
                          )}
                          {(isTestGame(g) || isImportedGame(g)) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteTestGame(g)}
                              title={isTestGame(g) ? "Test-Spiel löschen" : "Import-Spiel löschen"}
                              className="h-9 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredGames.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">
                        Keine Spiele in dieser Phase.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </TabsContent>

        {/* BONUS RESOLUTION */}
        <TabsContent value="bonus" className="mt-4">
          <section className="bg-card rounded-lg shadow-card p-5 space-y-4 max-w-2xl">
            <div>
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-accent" /> Bonus-Tipps auflösen
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Berechnet Bonus-Punkte (Weltmeister +20, Torschützenkönig +15, Gruppensieger je +5) für alle Tipper.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  🏆 Weltmeister (+20)
                </label>
                <select
                  value={bWorld}
                  onChange={(e) => setBWorld(e.target.value)}
                  className="w-full h-11 mt-1 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">—</option>
                  {allTeams.map(([name, flag]) => (
                    <option key={name} value={name}>
                      {flag} {teamDe(name)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  ⚽ Torschützenkönig (+15)
                </label>
                {/* Same picker the tippers used — free text could never match
                    the stored API-Football spelling ("A. Hadj-Moussa"). */}
                <div className="mt-1">
                  <PlayerCombobox value={bScorer} onChange={setBScorer} />
                </div>
              </div>
            </div>
            <details>
              <summary className="text-sm font-bold cursor-pointer">🥇 Gruppensieger A–L (je +5)</summary>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {WM_GROUPS.map((g) => (
                  <div key={g}>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Gruppe {g}</label>
                    {/* Only the teams that actually played in this group —
                        narrower than the old all-48 list, so a wrong-group
                        pick is impossible. */}
                    <select
                      value={bGroups[g] ?? ""}
                      onChange={(e) => setBGroups({ ...bGroups, [g]: e.target.value })}
                      className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs"
                    >
                      <option value="">—</option>
                      {teamOptions(bTeams[g] ?? [], bGroups[g] ?? "").map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.flag} {teamDe(t.name)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </details>

            {/* Austria-themed bonus questions — admin resolves each, +5 pts on exact match per tipper. */}
            <details className="border-t pt-3">
              <summary className="text-sm font-bold cursor-pointer">🇦🇹 Österreich-Spezial (6 Fragen · je +5)</summary>
              <div className="space-y-3 mt-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Kommt AT weiter als DE?</label>
                  <select
                    value={bAtBetterThanDe}
                    onChange={(e) => setBAtBetterThanDe(e.target.value as "" | "yes" | "no")}
                    className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs mt-1"
                  >
                    <option value="">— offen —</option>
                    <option value="yes">Ja</option>
                    <option value="no">Nein</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Gruppenphase: AT Platz</label>
                  <select
                    value={bAtGroupPosition}
                    onChange={(e) => setBAtGroupPosition(e.target.value as "" | "1" | "2" | "3" | "4")}
                    className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs mt-1"
                  >
                    <option value="">— offen —</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Erstes AT-Tor (Spieler)</label>
                  <div className="mt-1">
                    <PlayerCombobox value={bAtFirstGoalScorer} onChange={setBAtFirstGoalScorer} team="Austria" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Bester AT-Torschütze</label>
                  <div className="mt-1">
                    <PlayerCombobox value={bAtTopScorer} onChange={setBAtTopScorer} team="Austria" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">AT-Endstation im Turnier</label>
                  <select
                    value={bAtProgression}
                    onChange={(e) => setBAtProgression(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs mt-1"
                  >
                    <option value="">— offen —</option>
                    <option value="group">Gruppenphase</option>
                    <option value="r32">Sechzehntelfinale</option>
                    <option value="r16">Achtelfinale</option>
                    <option value="qf">Viertelfinale</option>
                    <option value="sf">Halbfinale</option>
                    <option value="final_loser">Finale (verloren)</option>
                    <option value="winner">Weltmeister</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">AT-Tore gesamt (ohne Elfm.)</label>
                  <Input
                    type="number"
                    min={0}
                    max={99}
                    value={bAtGoalsTotal}
                    onChange={(e) => setBAtGoalsTotal(e.target.value)}
                    placeholder="z. B. 7"
                    className="h-9 mt-1 tabular-nums"
                  />
                </div>
              </div>
            </details>

            <Button onClick={resolveBonus} disabled={bonusBusy} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              {bonusBusy ? "Berechne …" : "Punkte berechnen & vergeben"}
            </Button>

            {bonusResult && <BonusResolveResult res={bonusResult} />}
          </section>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          {/* Full Tipper management: search, role badges, grant/revoke
              admin + staff. Replaces the old read-only Username/XP list.
              The `users` state from refresh() is no longer used here. */}
          <UserManagement />
        </TabsContent>

        {/* STORY GENERATOR */}
        <TabsContent value="story" className="mt-4">
          <StoryGeneratorPanel />
        </TabsContent>

        {/* LIVE MONITOR */}
        <TabsContent value="monitor" className="mt-4">
          <MonitoringPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Inline live-mode panel for a single test game. Owns its own draft state
// so each row's inputs are independent and don't all re-render when one
// changes. The parent only knows about start/update/end commands.
function LiveControls({
  game: g,
  onStart,
  onUpdate,
  onEnd,
}: {
  game: any;
  onStart: (g: any) => void;
  onUpdate: (g: any, h: number, a: number, m: number) => void;
  onEnd: (g: any) => void;
}) {
  const [h, setH] = useState<string>(String(g.live_home_score ?? 0));
  const [a, setA] = useState<string>(String(g.live_away_score ?? 0));
  const [m, setM] = useState<string>(String(g.minute ?? 1));

  // Keep local drafts in sync if the parent refreshes after a successful save
  useEffect(() => { setH(String(g.live_home_score ?? 0)); }, [g.live_home_score]);
  useEffect(() => { setA(String(g.live_away_score ?? 0)); }, [g.live_away_score]);
  useEffect(() => { setM(String(g.minute ?? 1)); }, [g.minute]);

  const finished = !!g.is_finished;
  const live = !!g.is_live && !finished;

  return (
    <div className={`rounded-md border p-3 ${live ? "border-red-300 bg-red-50/40" : finished ? "border-border bg-muted/30" : "border-border bg-background"}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[10px] text-muted-foreground">#{g.game_no}</span>
          <span className="font-semibold text-sm truncate">
            {g.home_flag} {g.home_team} <span className="text-muted-foreground">vs.</span> {g.away_team} {g.away_flag}
          </span>
          {live && (
            <span className="text-[10px] font-bold bg-red-500 text-white rounded-pill px-1.5 py-0.5 inline-flex items-center gap-0.5">
              <span className="inline-block w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE {g.minute ? `· ${g.minute}'` : ""}
            </span>
          )}
          {finished && (
            <span className="text-[10px] font-bold bg-muted text-muted-foreground rounded-pill px-1.5 py-0.5 inline-flex items-center gap-0.5">
              <CheckCircle2 className="h-2.5 w-2.5" /> FT {g.home_score}:{g.away_score}
            </span>
          )}
        </div>
      </div>

      {!live && !finished && (
        <div className="mt-2">
          <Button
            size="sm"
            onClick={() => onStart(g)}
            className="h-8 bg-red-500 hover:bg-red-600 text-white font-bold gap-1.5"
          >
            <Radio className="h-3.5 w-3.5" /> Live starten
          </Button>
          <span className="ml-2 text-[11px] text-muted-foreground">Setzt is_live=true, Spielminute 1, 0:0 — Tipps sind ab Anpfiff −15 Min ohnehin gesperrt.</span>
        </div>
      )}

      {live && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <Input
            type="number" min={0} max={20}
            value={h}
            onChange={(e) => setH(e.target.value)}
            className="h-9 w-14 text-center font-bold"
          />
          <span className="font-bold">:</span>
          <Input
            type="number" min={0} max={20}
            value={a}
            onChange={(e) => setA(e.target.value)}
            className="h-9 w-14 text-center font-bold"
          />
          <span className="text-xs text-muted-foreground ml-1">Min</span>
          <Input
            type="number" min={1} max={130}
            value={m}
            onChange={(e) => setM(e.target.value)}
            className="h-9 w-16 text-center font-bold"
          />
          <Button
            size="sm"
            onClick={() => onUpdate(g, parseInt(h, 10), parseInt(a, 10), parseInt(m, 10))}
            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
          >
            Update Live
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEnd(g)}
            className="h-9 font-bold gap-1.5"
          >
            <CheckCircle2 className="h-4 w-4" /> Live beenden
          </Button>
        </div>
      )}
    </div>
  );
}

// Post-resolve receipt. The bonus questions are scored by EXACT string match
// against what tippers stored, so the failure mode is silent: a wrong-format
// answer scores zero while the request still succeeds. This panel makes every
// question's hit count visible, and flags any answer the server could not map
// onto a real team/player at all.
// Categories mirror the breakdown returned by services/bonusScoring.ts.
// "Treffer" = how many individual correct answers were counted across all
// tippers (Gruppensieger and Österreich aggregate their sub-questions).
const BONUS_QUESTION_LABELS: Record<string, string> = {
  worldChampion: "🏆 Weltmeister",
  topScorer: "⚽ Torschützenkönig",
  groupWinners: "🥇 Gruppensieger (A–L gesamt)",
  austria: "🇦🇹 Österreich-Spezial (gesamt)",
};

function BonusResolveResult({ res }: { res: any }) {
  const hits: Record<string, number> = res?.hits ?? {};
  const answered: Record<string, boolean> = res?.answered ?? {};
  const unmatched: { field: string; value: string }[] = res?.unmatched ?? [];
  const tippers: number = res?.tippers ?? 0;

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-display font-extrabold text-base">Auswertung</span>
        <span className="text-xs text-muted-foreground">
          <b className="text-foreground tabular-nums">{res?.awarded ?? 0}</b> von{" "}
          <b className="text-foreground tabular-nums">{tippers}</b> Tippern mit Punkten ·{" "}
          <b className="text-foreground tabular-nums">{res?.pointsTotal ?? 0}</b> Punkte gesamt
        </span>
      </div>

      {unmatched.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-1">
          <div className="text-xs font-bold text-destructive">
            ⚠️ Nicht zuordenbar — diese Antworten haben 0 Punkte vergeben:
          </div>
          {unmatched.map((u, i) => (
            <div key={i} className="text-[11px] text-foreground">
              <b>{BONUS_QUESTION_LABELS[u.field.split(".")[0]] ?? u.field}</b>
              {u.field.includes(".") ? ` (${u.field.split(".")[1]})` : ""}: „{u.value}"
            </div>
          ))}
          <div className="text-[11px] text-muted-foreground pt-1">
            Kein Team/Spieler mit diesem Namen in den Spieldaten. Bitte aus der Liste
            auswählen und erneut auflösen — die Berechnung ist wiederholbar.
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
        {Object.entries(BONUS_QUESTION_LABELS).map(([key, label]) => {
          const n = hits[key] ?? 0;
          // 0 hits is only suspicious for a question the admin actually
          // answered — an untouched category scoring nobody is correct.
          const zero = (answered[key] ?? false) && n === 0 && tippers > 0;
          return (
            <div key={key} className="flex items-baseline justify-between gap-2 text-xs py-0.5">
              <span className="text-muted-foreground truncate">{label}</span>
              <span className={`tabular-nums font-bold shrink-0 ${zero ? "text-destructive" : "text-foreground"}`}>
                {n}× {zero && "⚠️"}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        „0×" bei einer beantworteten Frage heißt: kein einziger Tipper lag richtig — bei
        vielen Tippern ein Hinweis auf einen falschen Wert.
      </p>
    </div>
  );
}
